import { convertToKg, formatWeightWithUnit } from "../../../../../utils/utils";
import { MassUnit, WasteFormValues, WasteRecord, WasteTotals } from "../types/wasteRecords.types";

/** Seeds the add/edit form from an existing record. */
export const recordToFormValues = (record: WasteRecord): WasteFormValues => ({
  streamName: record.streamName,
  managementType: record.managementType,
  distribution: record.distribution === "" ? "" : String(record.distribution),
  distributionUnit: record.distributionUnit,
  ingredientName: record.ingredientName || "",
  moisture: record.moisture === "" ? "" : String(record.moisture)
});

/** Folds submitted form values into the patch shape `onAdd`/`onUpdate` expect. */
export const formValuesToRecord = (values: WasteFormValues): Partial<WasteRecord> => ({
  streamName: values.streamName,
  managementType: values.managementType,
  distribution: values.distribution === "" ? "" : Number(values.distribution),
  distributionUnit: values.distributionUnit as MassUnit,
  ingredientName: values.ingredientName,
  moisture: values.moisture === "" ? "" : Number(values.moisture)
});

const toKgOrZero = (value: number | "", unit: MassUnit) =>
  convertToKg(typeof value === "number" ? value : 0, unit);

/**
 * Totals for the header readout. Display unit is ton when either the target
 * or any record uses ton; otherwise kg. `overTarget` compares in kg, so it is
 * unaffected by the display unit choice.
 */
export const computeWasteTotals = (
  records: WasteRecord[],
  wasteTarget: number | "",
  wasteTargetUnit: MassUnit
): WasteTotals => {
  const totalKg = records.reduce((sum, r) => sum + toKgOrZero(r.distribution, r.distributionUnit), 0);
  const targetKg = toKgOrZero(wasteTarget, wasteTargetUnit);

  const anyTon = wasteTargetUnit === "ton" || records.some(r => r.distributionUnit === "ton");
  const displayUnit: MassUnit = anyTon ? "ton" : "kg";

  return { totalKg, targetKg, displayUnit, overTarget: targetKg > 0 && totalKg > targetKg };
};

/**
 * Formats a kg amount for display in `displayUnit`.
 *
 * `formatWeightWithUnit` only formats — it does not convert — so the value
 * must be converted to the display unit here first. Without this, a total
 * computed from a ton-denominated record would render e.g. "2000.000 ton"
 * instead of "2.000 ton".
 */
export const formatDisplayWeight = (kg: number, displayUnit: MassUnit): string =>
  formatWeightWithUnit(displayUnit === "ton" ? kg / 1000 : kg, displayUnit);
